import {useState, useCallback} from 'react'
import type {OnboardingFormState, SetAttributeFormValue, IdentifierFormRow, RelationshipFormEntry} from '../types'
import {createInitialFormState, TEMPLATES, OnboardingStep} from '../types'
import {AppLocalDate} from '@/lib/date-utils'

const IdentifierType = {
    EMAIL: 'EMAIL', // eslint-disable-line i18next/no-literal-string -- typed constant
} as const

const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/
const SAFE_VALUE_RE = /^[a-zA-Z0-9@._-]+$/

/**
 * Central form state hook for the onboarding wizard.
 * Manages state across all 4 steps.
 */
export function useOnboardingForm() {
    const [form, setForm] = useState<OnboardingFormState>(createInitialFormState)

    const update = useCallback(<K extends keyof OnboardingFormState>(
        field: K,
        value: OnboardingFormState[K]
    ) => {
        setForm(prev => ({...prev, [field]: value, dirty: true}))
    }, [])

    const updateMultiple = useCallback((fields: Partial<OnboardingFormState>) => {
        setForm(prev => ({...prev, ...fields, dirty: true}))
    }, [])

    const setTemplate = useCallback((templateKey: string) => {
        const template = TEMPLATES.find(t => t.key === templateKey)
        if (!template) return
        setForm(prev => {
            const wasCustom = prev.templateKey !== templateKey
            return {
                ...prev,
                templateKey,
                identityType: template.identityType || prev.identityType,
                partyType: template.partyType,
                // Reset identity attributes when template changes (party attributes preserved)
                identityAttributes: wasCustom ? [] : prev.identityAttributes,
                templateSwitched: wasCustom && prev.dirty,
                dirty: true,
            }
        })
    }, [])

    const setIdentifiers = useCallback((identifiers: IdentifierFormRow[]) => {
        setForm(prev => ({...prev, identifiers, dirty: true}))
    }, [])

    const setRelationships = useCallback((relationships: RelationshipFormEntry[]) => {
        setForm(prev => ({...prev, relationships, dirty: true}))
    }, [])

    const setPartyAttributes = useCallback((partyAttributes: SetAttributeFormValue[]) => {
        setForm(prev => ({...prev, partyAttributes, dirty: true}))
    }, [])

    const setIdentityAttributes = useCallback((identityAttributes: SetAttributeFormValue[]) => {
        setForm(prev => ({...prev, identityAttributes, dirty: true}))
    }, [])

    const clearTemplateSwitched = useCallback(() => {
        setForm(prev => ({...prev, templateSwitched: false}))
    }, [])

    const reset = useCallback(() => {
        setForm(createInitialFormState())
    }, [])

    /**
     * Validate whether the user can proceed from a given step.
     */
    const canProceedFrom = useCallback((step: number): boolean => {
        switch (step) {
            case OnboardingStep.PERSON:
                if (form.mode === 'new') {
                    return true
                }
                return form.existingPartyId !== null
            case OnboardingStep.ORGANIZATION:
                return true
            case OnboardingStep.IDENTITY: {
                if (!form.identityType) return false
                // Entry date must not be in the past
                const today = AppLocalDate.today()
                if (form.entryDate && form.entryDate < today) return false
                // Exit date must not be before entry date
                if (form.exitDate && form.entryDate && form.exitDate < form.entryDate) return false
                // At least email identifier filled
                const emailRow = form.identifiers.find(r => r.identifierType === IdentifierType.EMAIL)
                if (!emailRow || !emailRow.identifierValue.trim()) return false
                // All identifier values must be non-empty and within length
                if (!form.identifiers.every(r => r.identifierValue.trim().length > 0 && r.identifierValue.length <= 500)) return false
                // All identifiers must pass format validation
                return form.identifiers.every(r => {
                    const val = r.identifierValue.trim()
                    if (r.identifierType === IdentifierType.EMAIL) return EMAIL_RE.test(val)
                    return SAFE_VALUE_RE.test(val)
                })
            }
            case OnboardingStep.REVIEW:
                return true
            default:
                return false
        }
    }, [form])

    return {
        form,
        setForm,
        update,
        updateMultiple,
        setTemplate,
        setIdentifiers,
        setRelationships,
        setPartyAttributes,
        setIdentityAttributes,
        clearTemplateSwitched,
        reset,
        canProceedFrom,
    }
}
